import {
  Direction,
  FocusableComponentLayout,
  FocusContext,
  FocusDetails,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import FocusableStripe, {
  FocusableStripeRefObject
} from 'common/components/stripe/FocusableStripe';
import { ViewportList, ViewportListRef } from 'common/components/viewport-list';
import variables from 'common/config/variables';
import {
  ContentAlign,
  ContentAxisAlignMap,
  ContentScrollingContentAxis,
  FocusableItem,
  ScrollPaddingAxisMap,
  StripeItemEvents
} from 'common/interfaces';
import { getDefaultSmoothScrollOptions, getKey, getRemoteKeyName } from 'common/utils';
import FocusHistory from 'common/utils/FocusHistory';
import useDisableFocusOutOfView from 'common/utils/hooks/useDisableFocusOutOfView';
import smoothScroll from 'common/utils/smoothScroll';
import { isNil } from 'lodash';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StripeProps } from './Stripe';
import StripePadding from './StripePadding';
import './StripeView.styles';

interface StripeHeaderProps {
  focusable?: boolean;
  onFocus?: (layout: FocusableComponentLayout, props: object, details: FocusDetails) => void;
}

// eslint-disable-next-line react/display-name
const StripeHeader = React.memo((props: React.PropsWithChildren<StripeHeaderProps>) => {
  const { ref, focusKey } = useFocusable({
    focusable: props.focusable,
    onFocus: props.onFocus
  });

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="stripe-header-container">
        {props.children}
      </div>
    </FocusContext.Provider>
  );
});

export interface StripeViewProps extends StripeItemEvents, FocusableItem {
  loading?: boolean;
  children?: React.ReactElement<StripeProps> | React.ReactElement<StripeProps>[] | React.ReactNode;
  axis?: ContentScrollingContentAxis;
  align?: ContentAlign;
  scrollPadding?: number;
  stripeAlign?: ContentAlign;
  stripeScrollPadding?: number;
  stripeInfiniteScroll?: boolean;
  forceLoadToIndex?: number;
  saveFocusOnUnmount?: boolean;
  stripeHeader?: React.ReactNode;
  onBack?: (last?: boolean) => boolean;
  loader?: React.ReactNode;
  emptyState?: React.ReactNode;
  disableVirtual?: boolean;
  onRowFocus?: (index: number, count: number) => void;
}

function StripeView(props: StripeViewProps) {
  const { recursiveCheck, stopChecking } = useDisableFocusOutOfView();
  const currentFocusedRow = useRef<number | undefined>(undefined);
  const [focusIndexes, setFocusIndexes] = useState<[number, number] | undefined>();
  // Private restrict focus boundary when focus is inside of the stripe view
  const [isFocusBoundary, setIsFocusBoundary] = useState(false);
  //
  const listRef = useRef<ViewportListRef | null>(null);
  const stripeRef = useRef<(FocusableStripeRefObject | undefined)[]>([]);
  // Invert axis in order to have the StripeView Scroll
  const contentAxis = useMemo(() => ((props.axis || 'x') === 'x' ? 'y' : 'x'), [props.axis]);
  const childrenArrayRef = useRef<React.ReactNode[]>([]);
  const childrenArray = useMemo(() => {
    const array = React.Children.toArray(props.children);
    childrenArrayRef.current = array;
    return array;
  }, [props.children]);

  const focusBoundaryDirections = useMemo(
    () =>
      isFocusBoundary
        ? ((contentAxis === 'x' ? ['left', 'right'] : ['up', 'down']) as Direction[])
        : undefined,
    [contentAxis, isFocusBoundary]
  );

  const { ref, focusKey, focusSelf } = useFocusable({
    focusKey: props.focusKey || location.pathname,
    focusable: !props.disabled,
    isFocusBoundary: isFocusBoundary || props.isFocusBoundary,
    focusBoundaryDirections: focusBoundaryDirections || props.focusBoundaryDirections,
    autoRestoreFocus: true,
    saveLastFocusedChild: true,
    trackChildren: false,
    onFocus: props.onFocus,
    onBlur: (...args) => {
      stopChecking();
      props.onBlur && props.onBlur(...args);
    }
  });
  const stripePaddingValue = useMemo(() => '100%', []);

  // Avoid missing focus
  useEffect(() => {
    const currentFocusKey = getCurrentFocusKey();
    if (isNil(currentFocusKey)) {
      startTransition(() => setFocusIndexes([0, 0]));
    }
  }, []);

  // Sync prop with inner state
  useEffect(() => {
    props.initiallyFocused && focusSelf();
  }, [props.initiallyFocused]);

  const onStripeHeaderFocus = useCallback((layout: FocusableComponentLayout) => {
    smoothScroll({
      ...getDefaultSmoothScrollOptions(layout.width),
      duration: variables.SCROLL_ANIMATION_DURATION,
      toElement: layout.node,
      scrollingElement: ref.current,
      paddingLeft: 0,
      paddingTop: 0
    });
  }, []);

  const onRowFocus = useCallback(
    (layout: FocusableComponentLayout, data: Partial<{ index: number }>) => {
      currentFocusedRow.current = data?.index || 0;
      props.onRowFocus?.(currentFocusedRow.current, childrenArrayRef.current.length);
      const focusBoundary =
        currentFocusedRow.current > 0 &&
        currentFocusedRow.current < childrenArrayRef.current.length - 1;
      setIsFocusBoundary(focusBoundary);
      recursiveCheck(layout.node, ref.current, layout.width, layout.height);
      //@ts-ignore
      smoothScroll({
        ...getDefaultSmoothScrollOptions(layout.width),
        duration: variables.SCROLL_ANIMATION_DURATION,
        toElement: layout.node,
        scrollingElement: ref.current,
        ...(props.align ? { [ContentAxisAlignMap[contentAxis]]: props.align } : undefined),
        ...(!isNil(props.scrollPadding)
          ? { [ScrollPaddingAxisMap[contentAxis]]: props.scrollPadding }
          : undefined)
      });
    },
    [props.align, props.scrollPadding, contentAxis, props.axis, props.onRowFocus]
  );

  const onRowBlur = useCallback(() => {
    currentFocusedRow.current = undefined;
  }, []);

  const onBack = useCallback(
    (status: number) => {
      const last = status === -1 && currentFocusedRow.current === 0;
      const onBackResponse = props.onBack ? props.onBack(last) : true;
      if (!onBackResponse) return;
      if (status === 0) {
        !isNil(currentFocusedRow.current) &&
          stripeRef.current?.[currentFocusedRow.current]?.scrollToIndex(0, true);
      } else if (status === -1 && currentFocusedRow.current === 0) {
        setFocus('MENU');
      } else if (status === -1) {
        if (props.disableVirtual) {
          stripeRef.current?.[0]?.scrollToIndex(0, false);
          stripeRef.current?.[0]?.stripeHeaderFocus?.(0);
        } else {
          listRef.current?.scrollToIndex({
            index: 0,
            alignToTop: true,
            prerender: childrenArrayRef.current.length - 1,
            animationDuration: variables.ROW_SCROLL_ANIMATION_DURATION,
            onScroll: () => {
              stripeRef.current?.[0]?.scrollToIndex(0, false);
              stripeRef.current?.[0]?.stripeHeaderFocus?.(0);
            }
          });
        }
      }
    },
    [props.disableVirtual]
  );

  const onScrollToIndex = useCallback(() => {
    // This function is a placeholder for future implementation
  }, []);

  const shouldRenderEmptyState = useMemo(() => {
    return !props.loading && childrenArray.length === 0 && !!props.emptyState;
  }, [props.loading, childrenArray.length, props.emptyState]);

  useEffect(() => {
    const onBackFromEmptyState = (e: KeyboardEvent) => {
      if (getRemoteKeyName(e.keyCode) === 'BACK') {
        const onBackResponse = props.onBack ? props.onBack(true) : true;
        if (!onBackResponse) return;
      }
    };
    if (shouldRenderEmptyState) {
      document.addEventListener('keydown', onBackFromEmptyState);
    }
    return () => {
      document.removeEventListener('keydown', onBackFromEmptyState);
    };
  }, [shouldRenderEmptyState]);

  const getStripeRef = useMemo(
    () => (index: number) => (customRef: FocusableStripeRefObject | null) => {
      stripeRef.current[index] = customRef === null ? undefined : customRef;
    },
    []
  );

  const renderStripe = useCallback(
    ({ props: childProps, key: childKey }: { props: StripeProps; key?: string }, index: number) => {
      const key = getKey('stripe', index, props.focusKey);
      const focusIndex =
        (!isNil(props.initialIndex) && {
          index: props.initialIndex as number,
          animateScroll: true
        }) ||
        (!isNil(focusIndexes) && index === focusIndexes[0]
          ? { index: focusIndexes[1], animateScroll: true }
          : undefined);
      const hasHistory = FocusHistory.getPreviousByPath(location.pathname);

      return (
        <FocusableStripe
          key={childKey || key}
          stripeKey={childProps.stripeKey}
          ref={getStripeRef(index)}
          items={childProps.items}
          title={childProps.title}
          renderItem={childProps.renderItem}
          visible={childProps.visible}
          index={index}
          onStripeItemPress={props.onStripeItemPress || childProps.onStripeItemPress}
          onFocus={onRowFocus}
          onBlur={onRowBlur}
          onStripeItemFocus={props.onStripeItemFocus || childProps.onStripeItemFocus}
          onStripeItemBlur={props.onStripeItemBlur || childProps.onStripeItemBlur}
          onStripeItemHover={props.onStripeItemHover || childProps.onStripeItemHover}
          getFocusKey={childProps.getFocusKey}
          saveLastFocusedChild={childProps.saveLastFocusedChild}
          onBack={onBack}
          initialIndex={focusIndex}
          initiallyFocused={props.initiallyFocused && index === 0 && !hasHistory}
          onScrollToIndex={onScrollToIndex}
          align={props.stripeAlign}
          axis={props.axis || 'x'}
          scrollPadding={childProps.scrollPadding || props.stripeScrollPadding}
          loadMore={childProps.loadMore}
          forceLoadToIndex={props.forceLoadToIndex}
          stripeItemClassName={props.stripeItemClassName}
          saveFocusOnUnmount={props.saveFocusOnUnmount}
          offsetTop={childProps.offsetTop}
          offsetBottom={childProps.offsetBottom}
          grid={childProps.grid}
          stripeFooter={childProps.stripeFooter}
        />
      );
    },
    [
      props.onStripeItemBlur,
      props.onStripeItemFocus,
      props.onStripeItemPress,
      props.onStripeItemHover,
      onScrollToIndex,
      props.stripeItemClassName,
      onBack,
      focusIndexes,
      props.stripeScrollPadding,
      props.forceLoadToIndex,
      props.axis,
      props.initialIndex,
      props.saveFocusOnUnmount,
      onRowFocus,
      onRowBlur
    ]
  );

  const renderView = () => {
    if (props.loading) return null;
    if (props.disableVirtual) {
      return childrenArray.map((item, index) => renderStripe(item as any, index));
    }
    return (
      <ViewportList ref={listRef} viewportRef={ref} items={childrenArray} axis={contentAxis}>
        {(item, index) => renderStripe(item as any, index)}
      </ViewportList>
    );
  };

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={`stripe-view-container ${contentAxis}`} ref={ref} key={focusKey}>
        {props.stripeHeader && (
          <StripeHeader focusable={!isFocusBoundary} onFocus={onStripeHeaderFocus}>
            {props.stripeHeader}
          </StripeHeader>
        )}
        {renderView()}
        {props.loading && props.loader}
        {shouldRenderEmptyState && props.emptyState}
        <StripePadding direction="bottom" padding={stripePaddingValue} />
      </div>
    </FocusContext.Provider>
  );
}

export default React.memo(StripeView);
